import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Mail, Phone, MapPin, Send, CheckCircle,
  Award, Briefcase, Network, Globe, Shield, Target,
  MessageCircle, ArrowRight, Handshake, ExternalLink,
  Settings, CreditCard, Building2, BookOpen, TrendingUp,
  Star, UserCheck, Megaphone, Link, Users, FileText,
  ChevronRight
} from 'lucide-react';
import { RiHandHeartLine } from 'react-icons/ri';
import { Header, Footer } from '@/components/Layout';
import { StatCard, TestimonialCard } from '@/components/Cards';
import {
  SERVICE_CATEGORIES, STATS, TESTIMONIALS, DIGITAL_MARKETING_TESTIMONIALS, INDUSTRY_LOGOS,
  COMPETENCIES, PARTNERS,
  ABOUT_OFFICE_IMAGE, BUSINESS_TEAM_IMAGE
} from '@/data/index';

const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

const ICON_MAP: Record<string, React.ElementType> = {
  Target, Settings, Shield, FileText, CreditCard, Building2,
  BookOpen, TrendingUp, Star, UserCheck, Megaphone, Link, Users,
  Handshake: RiHandHeartLine,
};

const WHY_US = [
  { icon: Award,     title: '14+ Years of Industry Expertise',    description: 'C-suite leadership across Deposit Money Banks, Microfinance Banking, Fintech, and Digital Marketing.' },
  { icon: Network,   title: '30,000+ Merchant Network',           description: 'Established network spanning all 6 geopolitical zones — enabling rapid merchant acquisition nationwide.' },
  { icon: Globe,     title: 'Nationwide Coverage',                description: 'Deep market intelligence from Lagos to Kano, Port Harcourt to Abuja, and every commercially vibrant location.' },
  { icon: Shield,    title: 'Regulatory & Compliance Authority',  description: 'Hands-on expertise in CBN, NIBSS, PCI-DSS, GDPR, and AML/CFT — keeping your business audit-ready.' },
  { icon: Briefcase, title: 'Banking & Fintech Intersection',     description: 'Uniquely positioned to bridge traditional banking institutions with modern payment technology and fintech operations.' },
  { icon: TrendingUp,title: '30M+ Monthly Impressions',           description: 'Proven digital marketing reach — helping brands market, advertise, sell and grow with outstanding conversion results.' },
];

const CAT_STYLES: Record<string, { badge: string; icon: string; bar: string; glow: string }> = {
  'Operations & Strategy':      { badge: 'bg-blue-50 text-blue-700 border-blue-200',     icon: 'text-blue-500',    bar: 'bg-blue-500',    glow: 'hover:shadow-blue-500/10' },
  'Fintech & Payments':         { badge: 'bg-violet-50 text-violet-700 border-violet-200', icon: 'text-violet-500', bar: 'bg-violet-500',  glow: 'hover:shadow-violet-500/10' },
  'Marketing & Communications': { badge: 'bg-amber-50 text-amber-700 border-amber-200',  icon: 'text-amber-500',   bar: 'bg-amber-500',   glow: 'hover:shadow-amber-500/10' },
  'Business Development':       { badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: 'text-emerald-500', bar: 'bg-emerald-500', glow: 'hover:shadow-emerald-500/10' },
};

export default function Home() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    const body = `Name: ${form.name}\nEmail: ${form.email}\n\nMessage:\n${form.message}`;
    window.open(`mailto:AOConsultingServ@gmail.com?subject=${encodeURIComponent(form.subject)}&body=${encodeURIComponent(body)}`, '_blank');
    setTimeout(() => { setSending(false); setSubmitted(true); setForm({ name: '', email: '', subject: '', message: '' }); }, 800);
  }, [form]);

  const openWhatsApp = useCallback(() => {
    window.open(`https://wa.me/2348036872163?text=${encodeURIComponent("Hello AO Consulting, I'd like to discuss a consulting engagement.")}`, '_blank');
  }, []);

  return (
    <>
      <Header />

      {/* ═══════════════════════════════════════════════════
          HERO — deep sky-blue gradient, bold 3-line headline
      ═══════════════════════════════════════════════════ */}
      <section id="hero" className="relative min-h-screen flex items-center overflow-hidden"
        style={{ background: 'oklch(0.42 0.16 222)' }}>

        {/* Layered background */}
        {/* Hero background — bright afrocentric boardroom, less covered */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1573496130103-a442a3754d0e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w4NDU2OTd8MHwxfHNlYXJjaHwzfHxBZnJpY2FuJTIwQmxhY2slMjBwcm9mZXNzaW9uYWxzJTIwYnVzaW5lc3MlMjBzdHJhdGVneSUyMHBsYW5uaW5nJTIwc2Vzc2lvbiUyMHdoaXRlYm9hcmQlMjBwcmVzZW50YXRpb258ZW58MHx8fHwxNzc1NDY5NjYzfDA&ixlib=rb-4.1.0&q=80&w=1920"
            alt="Professional Nigerian business team boardroom"
            className="w-full h-full object-cover object-center"
            style={{ opacity: 0.48 }}
            loading="eager" decoding="async"
          />
          {/* Lighter overlay — left-side text readability, right side shows image */}
          <div className="absolute inset-0"
            style={{ background: 'linear-gradient(105deg, oklch(0.38 0.16 225 / 0.90) 0%, oklch(0.42 0.17 220 / 0.65) 45%, oklch(0.32 0.14 228 / 0.28) 100%)' }} />
        </div>
        {/* Top accent line */}
        <div className="absolute top-0 left-0 right-0 h-[3px]"
          style={{ background: 'linear-gradient(90deg, transparent, oklch(0.83 0.12 218), oklch(0.95 0.05 200), oklch(0.83 0.12 218), transparent)' }} />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-16 pt-32 pb-24">

          {/* Two-column layout: text left, image visible right */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* LEFT — all text content */}
            <motion.div initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, ease: [0.16,1,0.3,1] }}
              className="flex flex-col items-start">

              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-7"
                style={{ background: 'oklch(1.0 0.0 0 / 0.12)', border: '1px solid oklch(1.0 0.0 0 / 0.20)' }}>
                <span className="w-2 h-2 rounded-full bg-sky-300 animate-pulse" />
                <span className="text-white/80 text-xs font-semibold tracking-widest uppercase">Strategic Business Consulting · Nigeria</span>
              </div>

              {/* Headline */}
              <h1 className="text-[44px] sm:text-5xl lg:text-[64px] font-extrabold text-white leading-[1.05] tracking-tight mb-6">
                Operational Excellence.<br />
                <span style={{ color: 'oklch(0.83 0.14 210)' }}>Strategic Growth.</span><br />
                <span style={{ color: 'oklch(0.83 0.14 210)' }}>Digital Strategy.</span>
              </h1>

              <p className="text-[17px] text-white/65 max-w-lg leading-relaxed mb-9">
                AO Consulting brings 14+ years of C-suite leadership in Banking, Fintech, Operations,
                and{' '}<span className="text-white/92 font-semibold">Digital Marketing</span>{' '}—
                turning complex challenges into clear, measurable results.
              </p>

              <div className="flex flex-wrap gap-3">
                <button onClick={() => scrollTo('contact')}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-bold text-sm
                             bg-white text-primary hover:bg-white/92 transition-all duration-200
                             shadow-xl shadow-black/25 active:scale-[0.97]">
                  Start a Conversation <ChevronRight size={15} />
                </button>
                <button onClick={openWhatsApp}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-sm
                             border border-green-300/40 text-green-200 hover:bg-green-500/12
                             hover:border-green-300/70 transition-all duration-200 active:scale-[0.97]">
                  <MessageCircle size={15} /> WhatsApp Us
                </button>
              </div>

              {/* Stats row — under CTAs */}
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.55 }}
                className="mt-12 pt-8 grid grid-cols-2 sm:grid-cols-4 gap-6 w-full"
                style={{ borderTop: '1px solid oklch(1.0 0.0 0 / 0.14)' }}>
                {STATS.map((s, i) => <StatCard key={s.label} value={s.value} label={s.label} index={i} />)}
              </motion.div>
            </motion.div>

            {/* RIGHT — empty on mobile, visually shows through image on desktop */}
            <div className="hidden lg:block" />
          </div>
        </div>

        {/* Scroll cue */}
        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1"
          animate={{ y: [0,8,0] }} transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}>
          <div className="w-px h-10" style={{ background: 'linear-gradient(to bottom, oklch(1.0 0.0 0 / 0.35), transparent)' }} />
        </motion.div>
      </section>

      {/* ═══════════════════════════════════════════════════
          TRUST STRIP — dark navy band
      ═══════════════════════════════════════════════════ */}
      <div style={{ background: 'oklch(0.16 0.028 235)' }} className="border-b border-white/8 py-4">
        <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
          <div className="flex items-center gap-5 flex-wrap">
            <span className="text-xs text-white/35 font-semibold uppercase tracking-widest whitespace-nowrap">Trusted by</span>
            <div className="h-4 w-px bg-white/10" />
            {INDUSTRY_LOGOS.map((item, i) => (
              <motion.span key={item.name} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
                viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="text-sm font-medium text-white/40 hover:text-white/80 transition-colors whitespace-nowrap cursor-default">
                {item.label}{i < INDUSTRY_LOGOS.length - 1 && <span className="ml-4 text-white/15">·</span>}
              </motion.span>
            ))}
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════
          ABOUT — sky-blue gradient left panel + white right
      ═══════════════════════════════════════════════════ */}
      <section id="about" className="py-0 overflow-hidden bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[640px]">

          {/* Left — rich sky-blue gradient with image */}
          <motion.div initial={{ opacity: 0, x: -32 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.65 }}
            className="relative overflow-hidden"
            style={{ background: 'linear-gradient(160deg, oklch(0.30 0.18 228) 0%, oklch(0.48 0.20 218) 60%, oklch(0.38 0.18 222) 100%)' }}>
            <img src={ABOUT_OFFICE_IMAGE} alt="Modern professional office"
              className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-luminosity"
              loading="lazy" decoding="async" />
            {/* Radial glow */}
            <div className="absolute top-1/4 left-1/4 w-80 h-80 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, oklch(0.75 0.17 218 / 0.25) 0%, transparent 70%)' }} />
            {/* Content inside */}
            <div className="relative z-10 p-10 lg:p-16 flex flex-col justify-end h-full min-h-[540px]">
              <div className="mt-auto">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                  style={{ background: 'oklch(1.0 0.0 0 / 0.12)', border: '1px solid oklch(1.0 0.0 0 / 0.18)' }}>
                  <span className="text-white/70 text-[10px] font-semibold uppercase tracking-widest">About AO Consulting</span>
                </div>
                <h2 className="text-3xl lg:text-[42px] font-extrabold text-white leading-[1.1] mb-4">
                  14 years of expertise,<br />built for your growth.
                </h2>
                <p className="text-white/60 text-sm leading-relaxed max-w-sm">
                  Banking · Fintech · Operations · Digital Marketing
                </p>
                {/* Profile chip */}
                <div className="mt-8 inline-flex items-center gap-3 px-4 py-3 rounded-2xl"
                  style={{ background: 'oklch(1.0 0.0 0 / 0.10)', backdropFilter: 'blur(12px)', border: '1px solid oklch(1.0 0.0 0 / 0.15)' }}>
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center font-bold text-primary text-xs flex-shrink-0">AO</div>
                  <div>
                    <p className="font-bold text-white text-sm">Ayoola Olalekan</p>
                    <p className="text-white/55 text-xs">COO · Founder, Seasoned Bankers Group</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right — white content panel */}
          <motion.div initial={{ opacity: 0, x: 32 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.65, delay: 0.1 }}
            className="flex flex-col justify-center px-8 py-16 lg:px-14 lg:py-20 bg-white">

            <p className="text-muted-foreground leading-relaxed mb-4">
              AO Consulting is led by <strong className="text-foreground">Ayoola Olalekan</strong>, a results-driven COO with over 14 years of progressive leadership across Deposit Money Banks, Microfinance Banking, high-growth Fintech companies, and Digital Marketing.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              A proven architect of operational excellence — delivering measurable improvements in payment efficiency, regulatory compliance, customer experience, and business growth. Deep expertise in NIP, NEFT, NAPS, SWIFT, Card, and POS infrastructure.
            </p>

            {/* Competency chips */}
            <div className="flex flex-wrap gap-2 mb-8">
              {COMPETENCIES.slice(0, 8).map((c) => (
                <span key={c} className="px-3 py-1.5 text-xs font-medium rounded-lg border transition-all duration-200
                                          bg-secondary text-foreground/65 border-border
                                          hover:bg-primary/8 hover:border-primary/25 hover:text-primary cursor-default">
                  {c}
                </span>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <button onClick={() => scrollTo('contact')}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm
                           bg-primary text-white hover:bg-primary/90 shadow-md shadow-primary/25
                           hover:shadow-lg hover:shadow-primary/30 transition-all duration-200 active:scale-[0.97]">
                Work with Us <ChevronRight size={15} />
              </button>
              <a
                href="https://ng.linkedin.com/in/ayoola-olalekan-709243170"
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm
                           border border-border text-foreground hover:border-primary/30 hover:bg-secondary
                           transition-all duration-200 active:scale-[0.97]">
                View Founder's LinkedIn <ExternalLink size={14} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SERVICES — sky-tinted section bg, coloured categories
      ═══════════════════════════════════════════════════ */}
      <section id="services" className="py-28 section-alt">
        <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.45 }} className="max-w-2xl mb-16">
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-primary/10 border border-primary/20
                             text-primary text-[11px] font-bold uppercase tracking-widest mb-5">
              What We Offer
            </span>
            <h2 className="text-3xl lg:text-[44px] font-extrabold text-foreground leading-[1.1] mb-4">
              A full suite of consulting services.
            </h2>
            <p className="text-muted-foreground text-[15px] leading-relaxed">
              Organised by practice area — from operations and fintech to marketing, digital strategy, and business development.
            </p>
          </motion.div>

          <div className="space-y-10">
            {SERVICE_CATEGORIES.map((cat, catIdx) => {
              const styles = CAT_STYLES[cat.category] || { badge: '', icon: 'text-primary', bar: 'bg-primary', glow: '' };
              return (
                <motion.div key={cat.category}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }} transition={{ duration: 0.45, delay: catIdx * 0.07 }}>

                  {/* Category header row */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-1.5 h-8 rounded-full ${styles.bar}`} />
                    <span className={`text-xs font-bold px-3.5 py-1.5 rounded-full border uppercase tracking-wider ${styles.badge}`}>
                      {cat.category}
                    </span>
                  </div>

                  {/* 4-col grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {cat.services.map((service, i) => {
                      const Icon = ICON_MAP[service.icon] || FileText;
                      return (
                        <motion.div key={service.id}
                          initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, margin: '-16px' }}
                          transition={{ duration: 0.35, delay: i * 0.06 }}
                          whileHover={{ y: -4, transition: { duration: 0.16 } }}
                          className={`bg-white rounded-2xl border border-border p-5
                                     hover:shadow-xl ${styles.glow}
                                     transition-all duration-250 group cursor-default`}>
                          {/* Icon */}
                          <div className="w-11 h-11 rounded-xl bg-secondary flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                            <Icon size={20} className={`${styles.icon} opacity-80`} />
                          </div>
                          <h3 className="font-semibold text-foreground text-[14px] leading-snug mb-2">{service.title}</h3>
                          <p className="text-muted-foreground text-xs leading-relaxed">{service.description}</p>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          PARTNERS — dark navy section, logo grid
      ═══════════════════════════════════════════════════ */}
      <section id="partners"
        style={{ background: 'linear-gradient(180deg, oklch(0.16 0.028 235) 0%, oklch(0.20 0.032 230) 100%)' }}
        className="py-20">
        <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.45 }} className="text-center mb-12">
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full mb-5"
              style={{ background: 'oklch(0.60 0.19 218 / 0.20)', border: '1px solid oklch(0.60 0.19 218 / 0.30)' }}>
              <Handshake size={11} className="text-sky-300" />
              <span className="text-sky-300 text-[11px] font-bold uppercase tracking-widest">Digital Marketing Partners</span>
            </span>
            <h2 className="text-3xl lg:text-[40px] font-extrabold text-white leading-[1.1] mb-4">
              Organisations that trust us.
            </h2>
            <p className="text-white/45 text-[15px] leading-relaxed max-w-xl mx-auto">
              AO Consulting has delivered results for leading financial institutions, corporates, and growing businesses across Nigeria.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 max-w-5xl mx-auto">
            {PARTNERS.map((p, i) => (
              <motion.div key={p.name}
                initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.07, duration: 0.35 }}
                whileHover={{ y: -4, scale: 1.03, transition: { duration: 0.15 } }}
                className="flex flex-col items-center gap-3 p-4 rounded-2xl cursor-default"
                style={{ background: 'oklch(1.0 0.0 0 / 0.06)', border: '1px solid oklch(1.0 0.0 0 / 0.12)' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'oklch(1.0 0.0 0 / 0.12)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'oklch(1.0 0.0 0 / 0.06)')}>
                {p.hasLogo ? (
                  <div className="w-full h-12 flex items-center justify-center px-2">
                    <img src={p.logo} alt={p.name}
                      onError={(e) => {
                        const fb = (p as { fallbackLogo?: string }).fallbackLogo;
                        if (fb) (e.target as HTMLImageElement).src = fb;
                      }}
                      className="max-h-10 max-w-full object-contain transition-all duration-300"
                      loading="lazy" />
                  </div>
                ) : (
                  <div className="w-12 h-10 rounded-xl flex items-center justify-center text-white text-xs font-bold shadow-sm"
                    style={{ backgroundColor: (p as { color?: string }).color }}>
                    {(p as { initials?: string }).initials}
                  </div>
                )}
                <span className="text-[10px] font-semibold text-white/45 text-center leading-tight">{p.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          MID CTA — vivid sky-to-deep gradient
      ═══════════════════════════════════════════════════ */}
      <section className="py-20 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, oklch(0.55 0.22 215) 0%, oklch(0.38 0.18 228) 100%)' }}>
        {/* Decorative orb */}
        <div className="absolute -right-32 -top-32 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, oklch(0.75 0.17 218 / 0.22) 0%, transparent 65%)' }} />
        <div className="absolute inset-0 opacity-[0.04]">
          <img src={BUSINESS_TEAM_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>

        <div className="relative z-10 container mx-auto px-6 lg:px-10 max-w-7xl">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-10">
            <div className="max-w-xl text-center lg:text-left">
              <h2 className="text-3xl lg:text-[44px] font-extrabold text-white leading-[1.1] mb-4">
                Ready to scale your business across Nigeria?
              </h2>
              <p className="text-white/55 leading-relaxed">
                Whether you need PTSP, PSSP, Agency Banking, VAS or MFB advisory, digital marketing strategy, merchant acquisition, or a complete operations overhaul — the expertise is here.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <button onClick={() => scrollTo('contact')}
                className="inline-flex items-center gap-2 px-7 py-4 rounded-xl font-bold text-sm
                           bg-white text-primary hover:bg-white/90 transition-all duration-200
                           shadow-xl shadow-black/20 active:scale-[0.97] whitespace-nowrap">
                  Get a Free Consultation <ArrowRight size={15} />
              </button>
              <button onClick={openWhatsApp}
                className="inline-flex items-center gap-2 px-7 py-4 rounded-xl font-semibold text-sm
                           border border-white/25 text-white hover:bg-white/12 hover:border-white/45
                           transition-all duration-200 active:scale-[0.97] whitespace-nowrap">
                <MessageCircle size={15} /> WhatsApp
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          WHY CHOOSE US — white section, coloured icon cards
      ═══════════════════════════════════════════════════ */}
      <section id="why-us" className="py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.45 }} className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-primary/10 border border-primary/20
                             text-primary text-[11px] font-bold uppercase tracking-widest mb-5">
              Why Choose Us
            </span>
            <h2 className="text-3xl lg:text-[44px] font-extrabold text-foreground leading-[1.1] mb-4">
              The AO Consulting advantage.
            </h2>
            <p className="text-muted-foreground text-[15px] leading-relaxed">
              More than advice — access, networks, and a proven track record that delivers real results.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_US.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div key={item.title}
                  initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-20px' }} transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex gap-4 p-6 rounded-2xl bg-white border border-border
                             hover:border-primary/25 hover:shadow-xl hover:shadow-primary/8
                             transition-all duration-250 group">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-250"
                    style={{ background: 'oklch(0.60 0.19 218 / 0.10)' }}>
                    <Icon size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground text-sm mb-1.5 group-hover:text-primary transition-colors">{item.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* ── DIGITAL MARKETING SPOTLIGHT BANNER ── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-30px' }} transition={{ duration: 0.55, delay: 0.1 }}
          className="mt-10 mx-6 lg:mx-0 rounded-3xl overflow-hidden relative"
          style={{ background: 'linear-gradient(135deg, oklch(0.32 0.18 228) 0%, oklch(0.50 0.21 215) 55%, oklch(0.38 0.19 222) 100%)' }}>

          {/* Radial glow */}
          <div className="absolute right-0 top-0 w-[420px] h-[420px] rounded-full pointer-events-none -translate-y-1/2 translate-x-1/3"
            style={{ background: 'radial-gradient(circle, oklch(0.75 0.17 218 / 0.22) 0%, transparent 65%)' }} />
          <div className="absolute left-0 bottom-0 w-[300px] h-[300px] rounded-full pointer-events-none translate-y-1/2 -translate-x-1/3"
            style={{ background: 'radial-gradient(circle, oklch(0.60 0.19 218 / 0.18) 0%, transparent 65%)' }} />

          <div className="relative z-10 p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">

              {/* Left — big stat + statement */}
              <div className="lg:col-span-3">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-5"
                  style={{ background: 'oklch(1.0 0.0 0 / 0.12)', border: '1px solid oklch(1.0 0.0 0 / 0.18)' }}>
                  <TrendingUp size={12} className="text-sky-300" />
                  <span className="text-sky-200 text-[11px] font-bold uppercase tracking-widest">Digital Marketing Impact</span>
                </div>

                {/* Stat */}
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="text-[56px] lg:text-[72px] font-extrabold text-white leading-none tracking-tight">30M+</span>
                  <span className="text-white/60 text-lg font-medium leading-tight">Monthly<br />Impressions</span>
                </div>

                {/* Refined statement */}
                <p className="text-white/85 text-[17px] leading-relaxed max-w-xl font-medium">
                  With over <span className="text-white font-bold">30 million monthly impressions</span>, AO Consulting has helped brands
                  market, advertise, sell, manage and promote their businesses across Nigeria —
                  delivering <span className="text-sky-200 font-semibold">outstanding conversion rates</span> and measurable growth.
                </p>
              </div>

              {/* Right — capabilities list */}
              <div className="lg:col-span-2">
                <p className="text-white/50 text-xs font-bold uppercase tracking-widest mb-4">What we do for your brand</p>
                <div className="grid grid-cols-1 gap-2.5">
                  {[
                    { icon: Megaphone,   label: 'Brand Marketing & Advertising' },
                    { icon: TrendingUp,  label: 'Performance Marketing & Sales' },
                    { icon: Star,        label: 'Influencer & Content Strategy' },
                    { icon: Globe,       label: 'Social Media Management' },
                    { icon: UserCheck,   label: 'Executive & Personal Branding' },
                    { icon: Target,      label: 'Campaign Management & PR' },
                  ].map(({ icon: Icon, label }) => (
                    <div key={label} className="flex items-center gap-3 group">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: 'oklch(1.0 0.0 0 / 0.12)' }}>
                        <Icon size={13} className="text-sky-300" />
                      </div>
                      <span className="text-white/75 text-sm font-medium group-hover:text-white transition-colors">{label}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => scrollTo('services')}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm
                             bg-white text-primary hover:bg-white/90 transition-all duration-200 shadow-lg active:scale-[0.97]">
                  View All Services <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </motion.div>

      </section>

      {/* ═══════════════════════════════════════════════════
          EXPERIENCE — sky-tinted section, sticky left panel
      ═══════════════════════════════════════════════════ */}


      {/* ═══════════════════════════════════════════════════
          TESTIMONIALS — white section, subtle sky quote marks
      ═══════════════════════════════════════════════════ */}
      <section id="testimonials" className="py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.45 }} className="text-center max-w-xl mx-auto mb-14">
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-primary/10 border border-primary/20
                             text-primary text-[11px] font-bold uppercase tracking-widest mb-5">
              Client Feedback
            </span>
            <h2 className="text-3xl lg:text-[44px] font-extrabold text-foreground leading-[1.1] mb-4">
              Trusted across Nigeria's financial sector.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {TESTIMONIALS.map((t, i) => <TestimonialCard key={t.name} testimonial={t} index={i} />)}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-8 italic">
            * Testimonials are representative. Names changed for confidentiality.
          </p>
        </div>
      </section>

      {/* ═══ DIGITAL MARKETING TESTIMONIALS ═══ */}
      <section id="dm-testimonials" className="py-28 section-alt">
        <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.45 }} className="max-w-2xl mb-14">
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-primary/10 border border-primary/20
                             text-primary text-[11px] font-bold uppercase tracking-widest mb-5">
              <TrendingUp size={11} /> Digital Marketing Results
            </span>
            <h2 className="text-3xl lg:text-[44px] font-extrabold text-foreground leading-[1.1] mb-4">
              What brands say about our digital marketing.
            </h2>
            <p className="text-muted-foreground text-[15px] leading-relaxed">
              From social media management and influencer campaigns to executive branding and PR — here's what our clients experienced.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl">
            {DIGITAL_MARKETING_TESTIMONIALS.map((t, i) => (
              <TestimonialCard key={t.name} testimonial={t} index={i} />
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-8 italic max-w-5xl">
            * Testimonials are representative of client experiences. Names changed for confidentiality.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          CONTACT — deep navy dark section
      ═══════════════════════════════════════════════════ */}
      <section id="contact"
        style={{ background: 'linear-gradient(180deg, oklch(0.14 0.028 238) 0%, oklch(0.18 0.030 232) 100%)' }}
        className="py-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
          {/* Section heading */}
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.45 }} className="text-center mb-14">
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full mb-5"
              style={{ background: 'oklch(0.60 0.19 218 / 0.18)', border: '1px solid oklch(0.60 0.19 218 / 0.28)' }}>
              <span className="text-sky-300 text-[11px] font-bold uppercase tracking-widest">Get in Touch</span>
            </span>
            <h2 className="text-3xl lg:text-[44px] font-extrabold text-white leading-[1.1] mb-3">Let's talk business.</h2>
            <p className="text-white/45 text-[15px]">Ready to grow, optimise, or expand? Reach out directly or fill the form.</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 max-w-5xl mx-auto">

            {/* Contact info */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5 }} className="lg:col-span-2 space-y-3">
              {[
                { icon: Mail,  label: 'Email',    value: 'AOConsultingServ@gmail.com', href: 'mailto:AOConsultingServ@gmail.com', mono: false },
                { icon: Phone, label: 'Phone',    value: '+234 803 687 2163',           href: 'tel:+2348036872163',              mono: true  },
              ].map(({ icon: Icon, label, value, href, mono }) => (
                <a key={label} href={href}
                  className="flex items-center gap-4 p-4 rounded-xl transition-all duration-200 group"
                  style={{ background: 'oklch(1.0 0.0 0 / 0.06)', border: '1px solid oklch(1.0 0.0 0 / 0.10)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'oklch(1.0 0.0 0 / 0.10)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'oklch(1.0 0.0 0 / 0.06)')}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'oklch(0.60 0.19 218 / 0.20)' }}>
                    <Icon size={16} className="text-sky-300" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-white/40 mb-0.5">{label}</p>
                    <p className={`text-sm font-medium text-white/85 truncate ${mono ? 'font-mono' : ''}`}>{value}</p>
                  </div>
                </a>
              ))}

              <button onClick={openWhatsApp}
                className="w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-200 group"
                style={{ background: 'oklch(0.50 0.18 150 / 0.12)', border: '1px solid oklch(0.50 0.18 150 / 0.20)' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'oklch(0.50 0.18 150 / 0.20)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'oklch(0.50 0.18 150 / 0.12)')}>
                <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <MessageCircle size={16} className="text-green-400" />
                </div>
                <div className="text-left">
                  <p className="text-xs text-white/40 mb-0.5">WhatsApp</p>
                  <p className="text-sm font-medium text-white/85">Chat on WhatsApp</p>
                </div>
              </button>

              <div className="flex items-center gap-4 p-4 rounded-xl"
                style={{ background: 'oklch(1.0 0.0 0 / 0.04)', border: '1px solid oklch(1.0 0.0 0 / 0.08)' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'oklch(0.60 0.19 218 / 0.15)' }}>
                  <MapPin size={16} className="text-sky-300" />
                </div>
                <div>
                  <p className="text-xs text-white/40 mb-0.5">Location</p>
                  <p className="text-sm font-medium text-white/85">Lagos, Nigeria</p>
                </div>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="lg:col-span-3">
              {submitted ? (
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                  className="h-full flex flex-col items-center justify-center text-center p-12 rounded-2xl min-h-[420px]"
                  style={{ background: 'oklch(1.0 0.0 0 / 0.06)', border: '1px solid oklch(1.0 0.0 0 / 0.10)' }}>
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 18, delay: 0.1 }}
                    className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                    <CheckCircle size={30} className="text-green-400" />
                  </motion.div>
                  <h3 className="text-xl font-bold text-white mb-2">Email app opened!</h3>
                  <p className="text-white/50 text-sm mb-6 max-w-xs">Your message is pre-filled — just hit send and we'll respond promptly.</p>
                  <div className="flex gap-3">
                    <button onClick={() => setSubmitted(false)}
                      className="px-5 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors">
                      Send Another
                    </button>
                    <button onClick={openWhatsApp}
                      className="px-5 py-2.5 border border-green-500/30 text-green-400 rounded-xl text-sm font-semibold
                                 hover:bg-green-500/10 flex items-center gap-2 transition-colors">
                      <MessageCircle size={14} />WhatsApp
                    </button>
                  </div>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="rounded-2xl p-8 space-y-5"
                  style={{ background: 'oklch(1.0 0.0 0 / 0.07)', border: '1px solid oklch(1.0 0.0 0 / 0.12)' }}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {[
                      { label: 'Full Name', key: 'name', type: 'text', placeholder: 'Your full name' },
                      { label: 'Email Address', key: 'email', type: 'email', placeholder: 'your@email.com' },
                    ].map(({ label, key, type, placeholder }) => (
                      <div key={key}>
                        <label className="block text-xs font-semibold text-white/50 mb-2 uppercase tracking-wider">{label} *</label>
                        <input type={type} required value={form[key as keyof typeof form]}
                          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                          placeholder={placeholder}
                          className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder:text-white/25
                                     focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                          style={{ background: 'oklch(1.0 0.0 0 / 0.08)', border: '1px solid oklch(1.0 0.0 0 / 0.15)' }} />
                      </div>
                    ))}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-white/50 mb-2 uppercase tracking-wider">Subject *</label>
                    <input type="text" required value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      placeholder="How can we help?"
                      className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder:text-white/25
                                 focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                      style={{ background: 'oklch(1.0 0.0 0 / 0.08)', border: '1px solid oklch(1.0 0.0 0 / 0.15)' }} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-white/50 mb-2 uppercase tracking-wider">Message *</label>
                    <textarea required rows={5} value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Tell us about your business needs, goals, or challenges..."
                      className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder:text-white/25
                                 focus:outline-none focus:ring-2 focus:ring-primary transition-all resize-none"
                      style={{ background: 'oklch(1.0 0.0 0 / 0.08)', border: '1px solid oklch(1.0 0.0 0 / 0.15)' }} />
                  </div>
                  <button type="submit" disabled={sending}
                    className="w-full py-3.5 bg-primary text-white rounded-xl font-bold text-sm
                               hover:bg-primary/90 transition-all flex items-center justify-center gap-2
                               disabled:opacity-50 shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/35 active:scale-[0.99]">
                    {sending
                      ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Opening email…</>
                      : <><Send size={15} />Send Message</>}
                  </button>
                  <p className="text-xs text-center text-white/30">
                    Opens your email app. Or{' '}
                    <button type="button" onClick={openWhatsApp} className="text-green-400 hover:underline font-semibold">
                      chat on WhatsApp
                    </button> instead.
                  </p>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Floating WhatsApp */}
      <motion.button onClick={openWhatsApp}
        initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2, type: 'spring', stiffness: 400, damping: 20 }}
        whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.92 }}
        aria-label="Chat on WhatsApp"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-green-500
                   hover:bg-green-400 shadow-xl shadow-green-500/35 flex items-center justify-center">
        <MessageCircle size={26} className="text-white" />
        <span className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-20 pointer-events-none" />
      </motion.button>
    </>
  );
}
